// ─────────────────────────────────────────────────────────────────────────────
// MobileClient — the mobile app's HTTP client. Data layer: a class, no DOM.
//
// The mobile app holds TWO credentials, and the split is the security design:
//
//   • a long-lived DEVICE credential in an httpOnly, Secure, SameSite=Strict
//     cookie scoped by `Path` to the refresh route alone. JavaScript cannot read
//     it, so XSS cannot steal it; and because every data route below is
//     bearer-authenticated rather than cookie-authenticated, those routes have
//     no CSRF surface at all.
//
//   • a short-lived ACCESS TOKEN held ONLY in memory on this instance. Never
//     `localStorage` — see the header of `ui_mobile_shell.ts`.
//
// ⚠ NEITHER IS THE PQFORCE BEARER TOKEN. That one never leaves the portal: it
// is sealed at rest in the device record and unsealed per request, server-side.
// If a future change puts it in a response body, the whole model is gone.
// ─────────────────────────────────────────────────────────────────────────────

export interface MobileClientOptions {
    /** Base URL of the mobile REST surface, e.g. "/api/". */
    baseUrl: string;
    /** Called when the session is gone for good, so the UI can show onboarding. */
    onSessionLost?: () => void;
    /**
     * The language the person chose on this device, or null to let the server use the account's.
     * Sent as `X-Pqf-Language` on every request, so the server's own sentences and the tenant's
     * state names follow the choice too. A function, because the choice can change mid-session.
     */
    language?: () => string | null;
}

/** The envelope every mobile route answers with — the portal's standard shape. */
export interface MobileResponse<T> {
    result: string;
    output: T;
}

export class MobileApiError extends Error {
    constructor(
        message: string,
        /** The HTTP status, or 0 when the request never got an answer. */
        readonly statusCode: number,
        /** The parsed body, when there was one. */
        readonly body: unknown = null,
    ) {
        super(message);
        this.name = "MobileApiError";
    }
}

type Method = "GET" | "POST" | "PUT" | "DELETE";

export class MobileClient {
    private accessToken: string | null = null;
    /** In-flight refresh, so a burst of 401s costs one refresh, not one each. */
    private refreshing: Promise<boolean> | null = null;

    constructor(private readonly options: MobileClientOptions) {}

    setAccessToken(token: string | null): void {
        this.accessToken = token;
    }

    hasSession(): boolean {
        return this.accessToken !== null;
    }

    /**
     * Exchange the device cookie for a fresh access token.
     *
     * Sends no Authorization header on purpose — this is the one route the
     * cookie authenticates, and presenting a stale bearer alongside it would let
     * a server-side ordering bug answer from the wrong credential.
     */
    async refreshSession(): Promise<boolean> {
        if (this.refreshing) return this.refreshing;

        this.refreshing = (async () => {
            try {
                const response = await fetch(this.url("auth/session"), {
                    method: "POST",
                    credentials: "same-origin",
                    headers: this.baseHeaders(),
                });
                if (!response.ok) return false;
                const body = (await response.json()) as MobileResponse<{ accessToken?: string }>;
                const token = body?.output?.accessToken;
                if (!token) return false;
                this.accessToken = token;
                return true;
            } catch {
                // A network failure is not proof the session is gone — the caller
                // distinguishes the two, because dropping a valid session every
                // time a train enters a tunnel would be its own bug.
                return false;
            } finally {
                this.refreshing = null;
            }
        })();

        return this.refreshing;
    }

    /**
     * One request, with a single refresh-and-retry on 401.
     *
     * ⚠ Exactly one retry. A refresh that yields a token the route still refuses
     * means the session is genuinely finished; looping would hammer the endpoint
     * and never terminate.
     */
    async request<T>(method: Method, path: string, body?: unknown): Promise<T> {
        let response = await this.send(method, path, body);

        if (response.status === 401 && (await this.refreshSession())) {
            response = await this.send(method, path, body);
        }

        if (response.status === 401) {
            this.accessToken = null;
            this.options.onSessionLost?.();
            throw new MobileApiError("Session expired", 401);
        }

        const parsed = await this.parse(response);

        if (!response.ok) {
            // `result` is the message the USER SEES — the portal's own convention
            // — so it is preferred over anything invented here.
            const message = (parsed as MobileResponse<unknown> | null)?.result || `Request failed (${response.status})`;
            throw new MobileApiError(message, response.status, parsed);
        }

        return (parsed as MobileResponse<T>).output;
    }

    get<T>(path: string): Promise<T> {
        return this.request<T>("GET", path);
    }

    post<T>(path: string, body?: unknown): Promise<T> {
        return this.request<T>("POST", path, body);
    }

    put<T>(path: string, body?: unknown): Promise<T> {
        return this.request<T>("PUT", path, body);
    }

    private baseHeaders(): Record<string, string> {
        const headers: Record<string, string> = { Accept: "application/json", "X-Pqf-Request": "1" };
        const language = this.options.language?.() ?? null;
        if (language) headers["X-Pqf-Language"] = language;
        return headers;
    }

    private async send(method: Method, path: string, body?: unknown): Promise<Response> {
        // X-Pqf-Request: the mobile router refuses a cookie-carrying POST without it. Every
        // *.pqforce.com host is the same site, so SameSite=Strict alone does not stop another one
        // from sending such a POST; a custom header does (request guard).
        const headers = this.baseHeaders();
        if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
        if (body !== undefined) headers["Content-Type"] = "application/json";

        try {
            return await fetch(this.url(path), {
                method,
                credentials: "same-origin",
                headers,
                body: body === undefined ? undefined : JSON.stringify(body),
            });
        } catch (error) {
            throw new MobileApiError(error instanceof Error ? error.message : "Network error", 0);
        }
    }

    private async parse(response: Response): Promise<unknown> {
        const text = await response.text();
        if (!text) return null;
        try {
            return JSON.parse(text);
        } catch {
            // A proxy or a WAF answering HTML is a real case — see the Barracuda
            // in front of the tenants. Keep the text so it reaches the log.
            return { result: text.slice(0, 200), output: null };
        }
    }

    private url(path: string): string {
        const base = this.options.baseUrl.endsWith("/") ? this.options.baseUrl : `${this.options.baseUrl}/`;
        return base + path.replace(/^\//, "");
    }
}
